import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';

const LeadDetailsModal = ({ lead, isOpen, onClose, onSave, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset form data whenever the lead changes
  useEffect(() => {
    if (lead) {
      setFormData({ ...lead });
      setEditMode(false);
      setSaveError(null);
    }
  }, [lead]);

  if (!isOpen || !lead) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'activity', label: 'Activity', icon: 'Clock' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' },
    { id: 'notes', label: 'Notes', icon: 'FileText' }
  ];

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closed-won', label: 'Closed Won' },
    { value: 'closed-lost', label: 'Closed Lost' },
    { value: 'converted', label: 'Converted' }
  ];

  const getStatusLabel = (value) => {
    return statusOptions?.find(o => o?.value === value)?.label || (value ? value?.charAt(0)?.toUpperCase() + value?.slice(1) : 'New');
  };

  const mockActivities = [
    {
      id: 1,
      type: 'email',
      description: 'Email sent: Follow-up on proposal',
      timestamp: lead?.lastInteraction || new Date()?.toISOString(),
      user: 'You'
    },
    {
      id: 2,
      type: 'call',
      description: 'Phone call: 15 minutes discussion about requirements',
      timestamp: lead?.createdAt || new Date()?.toISOString(),
      user: 'You'
    },
    {
      id: 3,
      type: 'note',
      description: 'Lead added to pipeline',
      timestamp: lead?.createdAt || new Date()?.toISOString(),
      user: 'System'
    }
  ];

  const mockCommunications = [
    {
      id: 1,
      type: 'email',
      subject: 'Re: Partnership Opportunity',
      preview: 'Thank you for reaching out. I\'m very interested in learning more about your services...',
      timestamp: lead?.lastInteraction || new Date()?.toISOString(),
      direction: 'inbound'
    },
    {
      id: 2,
      type: 'email',
      subject: 'Follow-up on our conversation',
      preview: 'Hi, Following up on our call. As discussed, I\'m attaching our proposal...',
      timestamp: lead?.createdAt || new Date()?.toISOString(),
      direction: 'outbound'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!lead?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const nameParts = (formData?.name || '')?.trim()?.split(' ');
      const firstName = nameParts?.[0] || '';
      const lastName = nameParts?.slice(1)?.join(' ') || '';

      const { error } = await supabase
        ?.from('funnel_leads')
        ?.update({
          first_name: firstName,
          last_name: lastName,
          email: formData?.email || '',
          phone: formData?.phone || '',
          company: formData?.company || '',
          job_title: formData?.title || '',
          lead_score: formData?.score || 0,
          total_value: formData?.estimatedValue || 0,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', lead?.id);

      if (error) throw error;

      setEditMode(false);
      if (onSave) onSave(formData);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving lead:', err);
      setSaveError(err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({ ...lead });
    setEditMode(false);
    setSaveError(null);
  };

  const handleCall = () => {
    const phone = lead?.phone;
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    } else {
      alert('No phone number available for this lead.');
    }
  };

  const handleEmail = () => {
    const email = lead?.email;
    if (email) {
      window.open(`mailto:${email}?subject=Following up&body=Hi ${lead?.name || ''},`, '_blank');
    } else {
      alert('No email address available for this lead.');
    }
  };

  const handleMessage = () => {
    const phone = lead?.phone;
    if (phone) {
      window.open(`sms:${phone}`, '_self');
    } else {
      alert('No phone number available for this lead.');
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email': return 'Mail';
      case 'call': return 'Phone';
      case 'note': return 'FileText';
      case 'meeting': return 'Calendar';
      default: return 'Activity';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString)?.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const currentData = editMode ? formData : lead;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center flex-shrink-0">
              {lead?.avatar ? (
                <Image
                  src={lead?.avatar}
                  alt={lead?.name || 'Lead avatar'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-lg font-bold text-primary">
                  {(lead?.name || 'U')?.charAt(0)?.toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{lead?.name || 'Unknown Lead'}</h2>
              <p className="text-muted-foreground text-sm">
                {[lead?.company, lead?.title]?.filter(Boolean)?.join(' • ') || 'No company info'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName={saving ? 'Loader' : 'Save'}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => setEditMode(true)}
              >
                Edit
              </Button>
            )}
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>
        </div>

        {/* Save Error */}
        {saveError && (
          <div className="mx-6 mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 flex-shrink-0">
            <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{saveError}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border flex-shrink-0">
          <div className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="text-sm font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Fields Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData?.name || ''}
                        onChange={(e) => handleInputChange('name', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground">
                        {lead?.name || <span className="text-muted-foreground">—</span>}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
                    {editMode ? (
                      <input
                        type="email"
                        value={formData?.email || ''}
                        onChange={(e) => handleInputChange('email', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground">
                        {lead?.email || <span className="text-muted-foreground">—</span>}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                    {editMode ? (
                      <input
                        type="tel"
                        value={formData?.phone || ''}
                        onChange={(e) => handleInputChange('phone', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground">
                        {lead?.phone || <span className="text-muted-foreground">—</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData?.company || ''}
                        onChange={(e) => handleInputChange('company', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground">
                        {lead?.company || <span className="text-muted-foreground">—</span>}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Job Title</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData?.title || ''}
                        onChange={(e) => handleInputChange('title', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground">
                        {lead?.title || <span className="text-muted-foreground">—</span>}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                    {editMode ? (
                      <select
                        value={formData?.status || 'new'}
                        onChange={(e) => handleInputChange('status', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {statusOptions?.map(opt => (
                          <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/30 text-foreground flex items-center justify-between">
                        <span>{getStatusLabel(lead?.status)}</span>
                        <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted rounded-lg p-5 text-center">
                  <p className="text-3xl font-bold text-foreground">{lead?.score ?? 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Lead Score</p>
                </div>
                <div className="bg-muted rounded-lg p-5 text-center">
                  <p className="text-3xl font-bold text-foreground">
                    ${(lead?.estimatedValue || 0)?.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Estimated Value</p>
                </div>
                <div className="bg-muted rounded-lg p-5 text-center">
                  <p className="text-3xl font-bold text-foreground">{lead?.interactions ?? 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Interactions</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Recent Activity</h3>
              {mockActivities?.map((activity) => (
                <div key={activity?.id} className="flex items-start space-x-3 p-4 bg-muted rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name={getActivityIcon(activity?.type)} size={14} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{activity?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateTime(activity?.timestamp)} • {activity?.user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Communication History</h3>
              {mockCommunications?.map((comm) => (
                <div key={comm?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Mail" size={16} className="text-primary" />
                      <span className="font-medium text-foreground text-sm">{comm?.subject}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        comm?.direction === 'inbound' ?'bg-green-100 text-green-800' :'bg-blue-100 text-blue-800'
                      }`}>
                        {comm?.direction}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(comm?.timestamp)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{comm?.preview}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Internal Notes</h3>
              <div className="bg-muted rounded-lg p-4">
                <textarea
                  className="w-full h-40 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 bg-background text-foreground text-sm"
                  placeholder="Add notes about this lead..."
                  defaultValue={lead?.notes || ''}
                />
                <div className="mt-3 flex justify-end">
                  <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30 flex-shrink-0">
          <div className="flex items-center space-x-3 text-xs text-muted-foreground">
            <span>Created: {formatDateTime(lead?.createdAt)}</span>
            <span>•</span>
            <span>Last Updated: {formatDateTime(lead?.lastInteraction || lead?.createdAt)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCall}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="Phone" size={15} />
              Call
            </button>
            <button
              onClick={handleEmail}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="Mail" size={15} />
              Email
            </button>
            <button
              onClick={handleMessage}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="MessageSquare" size={15} />
              Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;